import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import {
	IEditCustomerContext,
	EditCustomerContext,
} from 'modules/CustomersAndRestaurants/Customers/EditCustomerNewUi/editCustomerState/editCustomerContext';
import { BpaContext, bpaInitialState, IBpaTariff, TTypeOfModalWindow } from './bpaContext';
import { bpaReducer } from './bpaReducer';
import { BpaActionTypes } from './bpaActions';
import { IUpdateCustomerBPA, BpaPaymentSystem, CardBrands } from 'types';
import { NEW_BPA_BLOCK_PREFIX, NEW_BPA_TARIFF_PREFIX } from 'appConstants';
import { useFetchData } from 'hooks/useFetchData';
import { getCustomerBPA } from 'backend/customers';
import { v4 as uuidv4 } from 'uuid';

export const BpaState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(bpaReducer, bpaInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { customerId, visibleForm } = useContext<IEditCustomerContext>(EditCustomerContext);

	const needRequest = visibleForm === 'bpaForm' && customerId;

	const { response, refetch, loading } = useFetchData(needRequest ? getCustomerBPA : null, customerId || '');

	useEffect(() => {
		refetch();
	}, [needRequest]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: BpaActionTypes.REFETCH_DATA,
				payload: {
					needRefetchData: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		if (response) {
			const extendedBpaSets = response.data.bpaSets.map((bpaSet) => {
				const extendedTariffs: IBpaTariff[] = [];

				bpaSet.tariffs.forEach((tariff) => {
					const existingTariff = extendedTariffs.find(
						(extendedTariff) => extendedTariff.typeOfBpaPaymentSystem === tariff.bpaPaymentSystem,
					);

					if (existingTariff) {
						existingTariff.data.push(tariff);
					} else {
						extendedTariffs.push({
							id: uuidv4(),
							typeOfBpaPaymentSystem: tariff.bpaPaymentSystem,
							data: [tariff],
						});
					}
				});

				return { ...bpaSet, isOpenSet: false, tariffs: extendedTariffs };
			});

			dispatch({
				type: BpaActionTypes.CHANGE_BPA,
				payload: {
					bpa: extendedBpaSets,
				},
			});
		}
	}, [response]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: BpaActionTypes.REFETCH_DATA,
			payload: {
				needRefetchData: value,
			},
		});
	}, []);

	const addBpaBlock = useCallback(() => {
		dispatch({
			type: BpaActionTypes.ADD_BPA_BLOCK,
			payload: {
				bpa: {
					isOpenSet: true,
					id: `${NEW_BPA_BLOCK_PREFIX}/${uuidv4()}`,
					name: '',
					recipientAccountNumber: '',
					recipientName: '',
					recipientBank: '',
					recipientBankCity: '',
					recipientInn: '',
					recipientBic: '',
					recipientCorrespondentAccount: '',
					paymentDetails: '',
					cardContractNumber: '',
					sbpContractNumber: '',
					tariffs: [
						{
							id: uuidv4(),
							typeOfBpaPaymentSystem: null,
							data: [],
						},
					],
				},
			},
		});
	}, []);

	const deleteBpaBlock = useCallback(
		(setId: string) => {
			const updatedBpaBlocks = state.bpa.filter((el) => el.id !== setId);

			dispatch({
				type: BpaActionTypes.CHANGE_BPA,
				payload: {
					bpa: updatedBpaBlocks,
				},
			});
		},
		[state.bpa],
	);

	const toggleIsOpenBpaBlock = useCallback(
		(setId: string) => {
			const updatedBpaBlocks = state.bpa.map((el) =>
				el.id === setId ? { ...el, isOpenSet: !el.isOpenSet } : el,
			);

			dispatch({
				type: BpaActionTypes.CHANGE_BPA,
				payload: {
					bpa: updatedBpaBlocks,
				},
			});
		},
		[state.bpa],
	);

	const changeName = useCallback(
		({ setId, value }: { setId: string; value: string }) => {
			const updatedBpaBlocks = state.bpa.map((el) => (el.id === setId ? { ...el, name: value } : el));

			dispatch({
				type: BpaActionTypes.CHANGE_BPA,
				payload: {
					bpa: updatedBpaBlocks,
				},
			});
		},
		[state.bpa],
	);

	const changeRecipientAccountNumber = useCallback(
		({ setId, value }: { setId: string; value: string }) => {
			const updatedBpaBlocks = state.bpa.map((el) =>
				el.id === setId ? { ...el, recipientAccountNumber: value } : el,
			);

			dispatch({
				type: BpaActionTypes.CHANGE_BPA,
				payload: {
					bpa: updatedBpaBlocks,
				},
			});
		},
		[state.bpa],
	);

	const changeRecipientName = useCallback(
		({ setId, value }: { setId: string; value: string }) => {
			const updatedBpaBlocks = state.bpa.map((el) => (el.id === setId ? { ...el, recipientName: value } : el));

			dispatch({
				type: BpaActionTypes.CHANGE_BPA,
				payload: {
					bpa: updatedBpaBlocks,
				},
			});
		},
		[state.bpa],
	);

	const changeRecipientBank = useCallback(
		({ setId, value }: { setId: string; value: string }) => {
			const updatedBpaBlocks = state.bpa.map((el) => (el.id === setId ? { ...el, recipientBank: value } : el));

			dispatch({
				type: BpaActionTypes.CHANGE_BPA,
				payload: {
					bpa: updatedBpaBlocks,
				},
			});
		},
		[state.bpa],
	);

	const changeRecipientBankCity = useCallback(
		({ setId, value }: { setId: string; value: string }) => {
			const updatedBpaBlocks = state.bpa.map((el) =>
				el.id === setId ? { ...el, recipientBankCity: value } : el,
			);

			dispatch({
				type: BpaActionTypes.CHANGE_BPA,
				payload: {
					bpa: updatedBpaBlocks,
				},
			});
		},
		[state.bpa],
	);

	const changeRecipientInn = useCallback(
		({ setId, value }: { setId: string; value: string }) => {
			const updatedBpaBlocks = state.bpa.map((el) => (el.id === setId ? { ...el, recipientInn: value } : el));

			dispatch({
				type: BpaActionTypes.CHANGE_BPA,
				payload: {
					bpa: updatedBpaBlocks,
				},
			});
		},
		[state.bpa],
	);

	const changeRecipientBic = useCallback(
		({ setId, value }: { setId: string; value: string }) => {
			const updatedBpaBlocks = state.bpa.map((el) => (el.id === setId ? { ...el, recipientBic: value } : el));

			dispatch({
				type: BpaActionTypes.CHANGE_BPA,
				payload: {
					bpa: updatedBpaBlocks,
				},
			});
		},
		[state.bpa],
	);

	const changeRecipientCorrespondentAccount = useCallback(
		({ setId, value }: { setId: string; value: string }) => {
			const updatedBpaBlocks = state.bpa.map((el) =>
				el.id === setId ? { ...el, recipientCorrespondentAccount: value } : el,
			);

			dispatch({
				type: BpaActionTypes.CHANGE_BPA,
				payload: {
					bpa: updatedBpaBlocks,
				},
			});
		},
		[state.bpa],
	);

	const changePaymentDetails = useCallback(
		({ setId, value }: { setId: string; value: string }) => {
			const updatedBpaBlocks = state.bpa.map((el) => (el.id === setId ? { ...el, paymentDetails: value } : el));

			dispatch({
				type: BpaActionTypes.CHANGE_BPA,
				payload: {
					bpa: updatedBpaBlocks,
				},
			});
		},
		[state.bpa],
	);

	const changeCardContractNumber = useCallback(
		({ setId, value }: { setId: string; value: string }) => {
			const updatedBpaBlocks = state.bpa.map((el) =>
				el.id === setId ? { ...el, cardContractNumber: value } : el,
			);

			dispatch({
				type: BpaActionTypes.CHANGE_BPA,
				payload: {
					bpa: updatedBpaBlocks,
				},
			});
		},
		[state.bpa],
	);

	const changeSbpContractNumber = useCallback(
		({ setId, value }: { setId: string; value: string }) => {
			const updatedBpaBlocks = state.bpa.map((el) =>
				el.id === setId ? { ...el, sbpContractNumber: value } : el,
			);

			dispatch({
				type: BpaActionTypes.CHANGE_BPA,
				payload: {
					bpa: updatedBpaBlocks,
				},
			});
		},
		[state.bpa],
	);

	const addTariff = useCallback(
		(setId: string) => {
			const updatedBpaBlocks = state.bpa.map((el) =>
				el.id === setId
					? {
							...el,
							tariffs: [
								...el.tariffs,
								{
									id: uuidv4(),
									typeOfBpaPaymentSystem: null,
									data: [],
								},
							],
					  }
					: el,
			);

			dispatch({
				type: BpaActionTypes.CHANGE_BPA,
				payload: {
					bpa: updatedBpaBlocks,
				},
			});
		},
		[state.bpa],
	);

	const deleteTariff = useCallback(
		({ setId, tariffId }: { setId: string; tariffId: string }) => {
			const updatedBpaBlocks = state.bpa.map((el) =>
				el.id === setId
					? {
							...el,
							tariffs: el.tariffs.filter((tariff) => tariff.id !== tariffId),
					  }
					: el,
			);

			dispatch({
				type: BpaActionTypes.CHANGE_BPA,
				payload: {
					bpa: updatedBpaBlocks,
				},
			});
		},
		[state.bpa],
	);

	const changeBpaPaymentSystem = useCallback(
		({ setId, tariffId, value }: { setId: string; tariffId: string; value: BpaPaymentSystem }) => {
			const updatedBpaBlocks = state.bpa.map((el) =>
				el.id === setId
					? {
							...el,
							tariffs: el.tariffs.map((tariff) =>
								tariff.id === tariffId
									? {
											...tariff,
											typeOfBpaPaymentSystem: value,
											data: value
												? [
														{
															id: `${NEW_BPA_TARIFF_PREFIX}/${uuidv4()}`,
															bpaPaymentSystem: value,
															cardBrand: null,
															bankFee: null,
															kmFee: null,
														},
												  ]
												: [],
									  }
									: tariff,
							),
					  }
					: el,
			);

			dispatch({
				type: BpaActionTypes.CHANGE_BPA,
				payload: {
					bpa: updatedBpaBlocks,
				},
			});
		},
		[state.bpa],
	);

	const addCardBrand = useCallback(
		({ setId, tariffId }: { setId: string; tariffId: string }) => {
			const updatedBpaBlocks = state.bpa.map((el) =>
				el.id === setId
					? {
							...el,
							tariffs: el.tariffs.map((tariff) =>
								tariff.id === tariffId
									? {
											...tariff,
											data: [
												...tariff.data,
												{
													id: `${NEW_BPA_TARIFF_PREFIX}/${uuidv4()}`,
													bpaPaymentSystem: tariff.typeOfBpaPaymentSystem,
													cardBrand: null,
													bankFee: null,
													kmFee: null,
												},
											],
									  }
									: tariff,
							),
					  }
					: el,
			);

			dispatch({
				type: BpaActionTypes.CHANGE_BPA,
				payload: {
					bpa: updatedBpaBlocks,
				},
			});
		},
		[state.bpa],
	);

	const deleteCardBrand = useCallback(
		({ setId, tariffId, brandId }: { setId: string; tariffId: string; brandId: string }) => {
			const updatedBpaBlocks = state.bpa.map((el) =>
				el.id === setId
					? {
							...el,
							tariffs: el.tariffs.map((tariff) =>
								tariff.id === tariffId
									? {
											...tariff,
											data: tariff.data.filter((cardBrand) => cardBrand.id !== brandId),
									  }
									: tariff,
							),
					  }
					: el,
			);

			dispatch({
				type: BpaActionTypes.CHANGE_BPA,
				payload: {
					bpa: updatedBpaBlocks,
				},
			});
		},
		[state.bpa],
	);

	const changeCardBrand = useCallback(
		({
			setId,
			tariffId,
			brandId,
			value,
		}: {
			setId: string;
			tariffId: string;
			brandId: string;
			value: CardBrands;
		}) => {
			const updatedBpaBlocks = state.bpa.map((el) =>
				el.id === setId
					? {
							...el,
							tariffs: el.tariffs.map((tariff) =>
								tariff.id === tariffId
									? {
											...tariff,
											data: tariff.data.map((cardBrand) =>
												cardBrand.id === brandId
													? { ...cardBrand, cardBrand: value }
													: cardBrand,
											),
									  }
									: tariff,
							),
					  }
					: el,
			);

			dispatch({
				type: BpaActionTypes.CHANGE_BPA,
				payload: {
					bpa: updatedBpaBlocks,
				},
			});
		},
		[state.bpa],
	);

	const changeBankFee = useCallback(
		({
			setId,
			tariffId,
			brandId,
			value,
		}: {
			setId: string;
			tariffId: string;
			brandId: string;
			value: Nullable<number>;
		}) => {
			const updatedBpaBlocks = state.bpa.map((el) =>
				el.id === setId
					? {
							...el,
							tariffs: el.tariffs.map((tariff) =>
								tariff.id === tariffId
									? {
											...tariff,
											data: tariff.data.map((cardBrand) =>
												cardBrand.id === brandId ? { ...cardBrand, bankFee: value } : cardBrand,
											),
									  }
									: tariff,
							),
					  }
					: el,
			);

			dispatch({
				type: BpaActionTypes.CHANGE_BPA,
				payload: {
					bpa: updatedBpaBlocks,
				},
			});
		},
		[state.bpa],
	);

	const changeKMFee = useCallback(
		({
			setId,
			tariffId,
			brandId,
			value,
		}: {
			setId: string;
			tariffId: string;
			brandId: string;
			value: Nullable<number>;
		}) => {
			const updatedBpaBlocks = state.bpa.map((el) =>
				el.id === setId
					? {
							...el,
							tariffs: el.tariffs.map((tariff) =>
								tariff.id === tariffId
									? {
											...tariff,
											data: tariff.data.map((cardBrand) =>
												cardBrand.id === brandId ? { ...cardBrand, kmFee: value } : cardBrand,
											),
									  }
									: tariff,
							),
					  }
					: el,
			);

			dispatch({
				type: BpaActionTypes.CHANGE_BPA,
				payload: {
					bpa: updatedBpaBlocks,
				},
			});
		},
		[state.bpa],
	);

	const setDataForSubmit = useCallback((value: Nullable<IUpdateCustomerBPA>) => {
		dispatch({
			type: BpaActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: BpaActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<BpaContext.Provider
			value={{
				refetchData,
				addBpaBlock,
				deleteBpaBlock,
				toggleIsOpenBpaBlock,
				changeName,
				changeRecipientAccountNumber,
				changeRecipientName,
				changeRecipientBank,
				changeRecipientBankCity,
				changeRecipientInn,
				changeRecipientBic,
				changeRecipientCorrespondentAccount,
				changePaymentDetails,
				changeCardContractNumber,
				changeSbpContractNumber,
				addTariff,
				deleteTariff,
				changeBpaPaymentSystem,
				addCardBrand,
				deleteCardBrand,
				changeCardBrand,
				changeBankFee,
				changeKMFee,
				setDataForSubmit,
				changeTypeOfModalWindow,
				needRefetchData: state.needRefetchData,
				bpa: state.bpa,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</BpaContext.Provider>
	);
};
